import { useState, useMemo, useEffect } from 'react'
import type { MarketGateProps } from './MarketGate.types'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import { useAppContext } from 'context/AppContext/AppContext'
import RequestStatus from 'components/RequestStatus'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { Alert, AlertTitle, Box } from '@mui/material'
import type { Market } from 'types/Market'
import { useSearchParams } from 'react-router-dom'
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined'
// import { readQueryParams } from 'utils/generateQueryParams'
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined'

const MarketGate: <T>(props: MarketGateProps<T>) => JSX.Element = ({
  Component,
  componentProps,
  onlyFlow,
  titleTranslationCode,
  descriptionCode,
  allOption = false,
  actions,
  disableGateways,
  customFilter,
}) => {
  const { t } = useTranslation()
  const { markets, isMarketsLoading } = useAppContext()
  const [selectedMarketId, setSelectedMarketId] = useState<string>('')
  const [searchParams /*, _setSearchParams*/] = useSearchParams()

  const filterMarkets = (market: Market): boolean => {
    if (onlyFlow !== undefined && onlyFlow === 'gwp') {
      return market.flow === 'gwp'
    }
    if (onlyFlow !== undefined && onlyFlow === 'pd') {
      return market.flow === 'pd'
    }
    if (disableGateways) {
      return market.flow !== 'gateway'
    }
    if (customFilter) {
      return customFilter(market)
    }
    return true
  }

  useEffect(() => {
    if (markets !== undefined && !('error' in markets) && markets.data.length > 0) {
      const paramsMarketId = searchParams.get('marketId')
      if (paramsMarketId) {
        const marketExist = markets.data.some(market => market.id.toString() === paramsMarketId)
        if (marketExist || paramsMarketId === 'all') {
          setSelectedMarketId(paramsMarketId)
        } else {
          setSelectedMarketId(allOption ? 'all' : '')
        }
      } else {
        if (allOption) {
          setSelectedMarketId('all')
        } else {
          const firstMarketId = markets.data.filter(filterMarkets)?.[0]?.id.toString()
          if (firstMarketId) {
            setSelectedMarketId(firstMarketId)

            // setSearchParams(prev => ({ ...readQueryParams(prev), marketId: firstMarketId }))
          }
        }
      }
    } else {
      setSelectedMarketId(allOption ? 'all' : '')
    }
  }, [markets, searchParams])

  const handleChangeMarketId = (value: string): void => {
    setSelectedMarketId(value)
    // setSearchParams(prev => ({ ...readQueryParams(prev), marketId: value !== 'all' ? value : '' }))
  }

  const selectedMarket = useMemo(() => {
    if (markets === undefined || 'error' in markets) {
      return undefined
    }

    return markets.data.filter(filterMarkets).find(el => el.id.toString() === selectedMarketId.toString())
  }, [selectedMarketId, markets])

  if (markets === undefined || 'error' in markets) {
    return <RequestStatus data={markets} isLoading={isMarketsLoading} />
  }

  const filteredMarkets = markets.data.filter(filterMarkets).sort((a, b) => a.name.localeCompare(b.name))

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%" pb={2}>
        {!!titleTranslationCode && (
          <Box>
            <Typography variant="h5" component="h2" fontWeight={600}>
              {t(titleTranslationCode)}
            </Typography>
            {!!descriptionCode && (
              <Typography variant="body2" component="p" fontWeight={400} color="grey.600">
                {t(descriptionCode)}
              </Typography>
            )}
          </Box>
        )}
        <Box
          display="flex"
          gap={1}
          alignItems="flex-start"
          justifyContent="space-between"
          width={!!titleTranslationCode ? 'auto' : '100%'}
        >
          <FormControl sx={{ width: 300 }}>
            <InputLabel id="market-label">{t('common.selectMarket')}</InputLabel>
            <Select
              startAdornment={<LanguageOutlinedIcon sx={{ color: 'grey.500' }} />}
              labelId="market-label"
              id="market-select"
              size="small"
              value={selectedMarketId}
              label={t('common.selectMarket')}
              onChange={e => {
                handleChangeMarketId(e.target.value)
              }}
              MenuProps={{ sx: { maxHeight: 285 } }}
            >
              {filteredMarkets.map(market => (
                <MenuItem key={`${market.code}-${market.parentCode}-${market.description}`} value={market.id}>
                  <Typography fontSize={14} fontWeight={500} color="grey.800">
                    {market.name}
                    {!!market.description && (
                      <Typography display="block" color="grey.400" variant="caption" lineHeight={1} fontWeight={400}>
                        {market.description}
                      </Typography>
                    )}
                  </Typography>
                </MenuItem>
              ))}
              {allOption !== undefined && allOption ? <MenuItem value="all">{t('common.all')}</MenuItem> : null}
            </Select>
          </FormControl>
          {actions}
        </Box>
      </Box>
      {selectedMarket !== undefined || selectedMarketId !== '' || allOption ? (
        <div>
          <Component market={selectedMarket} selectedMarketId={selectedMarketId || 'all'} {...componentProps} />
        </div>
      ) : (
        <Alert severity="error" icon={<FolderOffOutlinedIcon />} sx={{ mt: 2 }}>
          <AlertTitle>{t('dialogs.marketNotFound')}</AlertTitle>
        </Alert>
      )}
    </>
  )
}

export default MarketGate
